import { getPanelTop } from 'weblite-router'
import { Room } from '@weblite/ts-action/room'
import { asyncDelay } from '@weblite/logic-toolkit'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { showUserActiveSessionsLimitModal } from '$comp/modals/user-active-sessions-limit'
import {
  getRoom,
  getRoomPermissions,
  isClientsRoom,
  RoomPermission,
  storageByRoomType,
} from '$logic/room'
import { getSubscription, setSubscriptionUnreadMark } from '$logic/subscription'
import { getPv, setPvUnreadMark } from '$logic/pv'
import { fetchUserStatus } from '$logic/users'
import { setPrivateSpaceUnreadMark } from '$logic/private-space'
import { setWappRoomUnreadMark } from '$logic/wapp-room'
import { openedRoomMessagesStorage } from '$logic/opened-room/opened-room-messages.storage'
import { goBackInRoomPanel, openRoomPage } from '$logic/router'
import { initiateRoomPinMessages, removeRoomPinsSync } from '$logic/pin'
import { shouldShowPremiumTrialModal } from '$logic/premium-features/service'
import { showStartPremiumFeaturesTrialModal } from '$comp/modals/start-premium-features-trial'
import { checkClientUserAccessToHallBasedOnSessionsLimit } from '$logic/hall/hall.service'
import { openedRoomStorage } from './opened-room.storage'
import {
  fetchMessagesFromLastOpenedRoomMessageStorage,
  initiateRoomMessage,
} from './opened-room-messages.service'
import { lastOpenedRoomMessagesService } from '$logic/last-opened-room-messages/service'
import { sendFieldApis } from '$comp/_app/components/chat/components/footer/components/send-field'
import { isDesktopSize } from '$helper/functions/environmentInfo'
import { showAccessToHallRestrictedBecauseOfPaymentModal } from '$comp/modals/access-to-hall-restricted-because-of-late-payment'
import { showErrorSnackbar } from '$comp/snackbars'
import { t } from '$i18n'

export async function onOpenRoom(roomId: string, openingIid?: number) {
  removeRoomPinsSync()

  // TODO: Check for performance effects! before loading messages, we are checking a lot of things!!!
  openedRoomStorage.setLoading(true)
  const room = await getRoom(roomId)

  if (await checkIfRoomIsRestricted(room)) {
    openedRoomStorage.setLoading(false)
    return
  }

  if (room?.type === 'CHANNEL') {
    const hasAccessToHall =
      await checkClientUserAccessToHallBasedOnSessionsLimit(roomId)

    // NOTE: 'TOO_MANY_REQUESTS' now only happens when we were throttled AND
    // have no previous answer to fall back on, so the device count is genuinely
    // unknown. Letting the room open here would turn "refresh until the check
    // fails" into a way around the limit, so we stop — but unlike before, we
    // say why instead of leaving a silently empty channel behind.
    if (hasAccessToHall === 'TOO_MANY_REQUESTS') {
      analite('SESSIONS_LIMIT_CHECK_THROTTLED', { roomId })
      showErrorSnackbar(t('activeSessions.tooManyRequests'))
      openedRoomStorage.setLoading(false)
      await goBackInRoomPanel()
      return
    }

    if (!hasAccessToHall) {
      const shouldContinue = await showUserActiveSessionsLimitModal({
        onRegister: async () => {
          await closeCurrentModal()
        },
        onCancel: async () => {
          await closeCurrentModal()
          await goBackInRoomPanel()
        },
      })

      if (!shouldContinue) {
        openedRoomStorage.setLoading(false)
        return
      }
    }
  }

  const shouldUsePreloadedMessages = openingIid
    ? false
    : fetchMessagesFromLastOpenedRoomMessageStorage(roomId)

  //  NOTE: load Messages
  await initiateRoomMessage(roomId, openingIid, shouldUsePreloadedMessages)
  initiateRoomPinMessages(roomId)

  if (!room) return

  if (room.type === 'PV') {
    //  NOTE: If room is PV, send request for user status
    const pv = await getPv(roomId)
    fetchUserStatus(pv?.to)
    setPvUnreadMark(roomId, false)
  } else if (room.type === 'WAPP_ROOM') setWappRoomUnreadMark(roomId, false)
  else if (room.type === 'PRIVATE_SPACE') setPrivateSpaceUnreadMark(false)
  else if (['GROUP', 'CHANNEL'].includes(room.type)) {
    // ignore if public hall
    if (isClientsRoom(roomId)) setSubscriptionUnreadMark(roomId, false)
    if (
      room.type === 'CHANNEL' &&
      (await shouldShowPremiumTrialModal(roomId)) &&
      import.meta.env.VITE_REJECT_NEW_CUSTOMERS !== 'true'
    ) {
      showStartPremiumFeaturesTrialModal()
    }
  }
}

async function checkIfRoomIsRestricted(room: Room | undefined) {
  if (room?.type === 'CHANNEL' || room?.type === 'GROUP') {
    const subscription = await getSubscription(room.id)
    if (subscription?.suspended) {
      await asyncDelay(300)
      showAccessToHallRestrictedBecauseOfPaymentModal({
        hallType: room.type,
        onClose: async () => {
          await closeCurrentModal()
          goBackInRoomPanel()
        },
      })
      return true
    }
  }

  return false
}

export async function openRoom(roomId: string, openingIid?: number) {
  if (!(await canSwitchRoom())) return

  const openedRoomId = getOpenedRoomId()

  const room = await getRoom(roomId)
  if (!room || (roomId === openedRoomId && !openingIid)) return

  if (!(openingIid && roomId === openedRoomId) && isDesktopSize())
    // some times we reopen room with openingIid so we don't need to sync because just room scrolls
    lastOpenedRoomMessagesService.syncOpenedRoomWithLastOpenedRoomMessages()

  openedRoomMessagesStorage.deleteAll()

  openRoomPage(roomId)
  await onOpenRoom(roomId, openingIid)
}

async function canSwitchRoom() {
  const isRecording = await sendFieldApis.checkVoiceStatus()
  if (isRecording) return false
  return true
}

export function getOpenedRoomId() {
  return getPanelTop('room')?.state?.roomId || null
}

export function getOpenedRoom() {
  const roomId = getOpenedRoomId()
  if (roomId) return getRoom(roomId)
  return null
}

export async function getOpenedRoomData() {
  const room = await getOpenedRoom()
  if (!room) return null
  const { get } = storageByRoomType(room.type)
  return get(room.id)
}

export async function openedRoomPermissions(): Promise<RoomPermission> {
  const roomId = getOpenedRoomId()
  return getRoomPermissions(roomId)
}
